import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageContainer from '@/components/layout/page-container';
import { Heading } from '@/components/ui/heading';
import { useAuthStore } from '@/store/auth';
import { useGetInquiry, useReplyInquiry } from '@/services/inquiry/queries';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Mail, 
  Calendar, 
  Clock, 
  CheckCircle,
  ArrowLeft,
  Send
} from 'lucide-react';
import { cn } from '@/lib/utils';

export default function ReplyInquiryPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const context = useAuthStore((state) => state.context);
  const [response, setResponse] = useState('');

  // Only allow workspace users to access this page
  if (context !== 'workspace') {
    return (
      <PageContainer scrollable={true}>
        <div className='flex h-full items-center justify-center'>
          <div className='text-center'>
            <h2 className='text-2xl font-semibold text-gray-900'>Access Denied</h2>
            <p className='mt-2 text-gray-600'>
              This page is only available for workspace users.
            </p>
          </div>
        </div>
      </PageContainer>
    );
  }

  const { data: inquiryData, isLoading, error } = useGetInquiry(id!, {
    enabled: !!id
  });

  const inquiry = inquiryData?.data;

  const { mutate: replyInquiry, isPending } = useReplyInquiry(
    () => {
      toast.success('Reply sent successfully');
      navigate('/dashboard/inquiries');
    },
    (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to send reply');
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!response.trim()) {
      toast.error('Please enter a response');
      return;
    }
    replyInquiry({ inquiryId: id!, response: response.trim() });
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const isResolved = status === 'resolved';
    return (
      <Badge
        variant='outline'
        className={cn(
          'flex items-center gap-1 border-0 text-xs font-medium',
          isResolved
            ? 'bg-green-50 text-green-700 hover:bg-green-100'
            : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
        )}
      >
        {isResolved ? (
          <CheckCircle className='h-3 w-3' />
        ) : (
          <Clock className='h-3 w-3' />
        )}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <PageContainer scrollable={true}>
        <div className='space-y-4'>
          <div className='h-8 bg-gray-200 rounded animate-pulse' />
          <div className='h-96 bg-gray-200 rounded animate-pulse' />
        </div>
      </PageContainer>
    );
  }

  if (error || !inquiry) {
    return (
      <PageContainer scrollable={true}>
        <div className='flex h-full items-center justify-center'>
          <div className='text-center'>
            <h2 className='text-2xl font-semibold text-gray-900'>Inquiry Not Found</h2>
            <p className='mt-2 text-gray-600'>
              The inquiry you're looking for doesn't exist or has been removed.
            </p>
            <Button 
              onClick={() => navigate('/dashboard/inquiries')}
              className='mt-4'
            >
              <ArrowLeft className='mr-2 h-4 w-4' />
              Back to Inquiries
            </Button>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer scrollable={true}>
      <div className='mb-5 flex items-center justify-between px-4'>
        <Heading 
          title='Reply to Inquiry' 
          description='Respond to user inquiry' 
        />
        <Button
          variant='outline'
          size='sm'
          onClick={() => navigate('/dashboard/inquiries')}
        >
          <ArrowLeft className='mr-2 h-4 w-4' />
          Back
        </Button>
      </div>
      
      <div className='space-y-6 px-4'>
        {/* Inquiry Details */}
        <Card>
          <CardHeader>
            <div className='flex items-center justify-between'>
              <CardTitle className='flex items-center gap-2'>
                <MessageSquare className='h-5 w-5' />
                Inquiry Details
              </CardTitle>
              {getStatusBadge(inquiry.status)}
            </div>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <Label className='text-sm font-medium text-gray-700'>Email</Label>
                <div className='flex items-center gap-2 mt-1'>
                  <Mail className='h-4 w-4 text-gray-500' />
                  <span className='text-sm'>{inquiry.email}</span>
                </div>
              </div>
              <div>
                <Label className='text-sm font-medium text-gray-700'>Created</Label>
                <div className='flex items-center gap-2 mt-1'>
                  <Calendar className='h-4 w-4 text-gray-500' />
                  <span className='text-sm'>{formatDate(inquiry.createdAt)}</span>
                </div>
              </div>
            </div>
            
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <Label className='text-sm font-medium text-gray-700'>Subject</Label>
                <p className='mt-1 text-sm font-medium'>
                  {inquiry.subject || 'No Subject'}
                </p>
              </div>
              <div>
                <Label className='text-sm font-medium text-gray-700'>Organisation</Label>
                <p className='mt-1 text-sm font-medium'>
                  {inquiry.user?.organisation?.name || 'No Organisation'}
                </p>
              </div>
            </div>

            <div>
              <Label className='text-sm font-medium text-gray-700'>Message</Label>
              <div className='mt-1 p-3 bg-gray-50 rounded-md'>
                <div 
                  className='text-sm prose prose-sm max-w-none'
                  dangerouslySetInnerHTML={{ __html: inquiry.message }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reply Form or Response Display */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Send className='h-5 w-5' />
              {inquiry.status === 'resolved' ? 'Response' : 'Send Reply'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {inquiry.status === 'resolved' ? (
              <div className='space-y-4'>
                <div>
                  <Label className='text-sm font-medium text-gray-700'>Response</Label>
                  <div className='mt-1 p-3 bg-green-50 rounded-md border border-green-200'>
                    <div 
                      className='text-sm prose prose-sm max-w-none'
                      dangerouslySetInnerHTML={{ __html: inquiry.response || 'No response provided' }}
                    />
                  </div>
                  {inquiry.resolvedAt && (
                    <p className='mt-2 text-xs text-gray-500'>
                      Resolved on {formatDate(inquiry.resolvedAt)}
                    </p>
                  )}
                </div>
                
                <div className='flex justify-end'>
                  <Button
                    variant='outline'
                    onClick={() => navigate('/dashboard/inquiries')}
                  >
                    Back to Inquiries
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className='space-y-4'>
                <div>
                  <Label htmlFor='response'>Response</Label>
                  <Textarea
                    id='response'
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    placeholder='Enter your response to the inquiry...'
                    className='mt-1 min-h-[120px]'
                    disabled={isPending}
                  />
                </div>
                
                <div className='flex justify-end gap-2'>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => navigate('/dashboard/inquiries')}
                    disabled={isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    type='submit'
                    disabled={isPending || !response.trim()}
                    className='flex items-center gap-2'
                  >
                    <Send className='h-4 w-4' />
                    {isPending ? 'Sending...' : 'Send Reply'}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
